import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { ArrowLeft } from 'lucide-react';
import { EntityPicker } from './EntityPicker';
import { paymentStatusFor } from '../../utils/payments';

/**
 * Dedicated Sales Payment form (issue: "Sales Order/Invoice details not
 * available when creating a payment; payment not reflected in the invoice").
 * Pick a Sales Invoice → see its total / paid / outstanding → record a
 * payment that POSTs a SalesPayment AND updates the invoice's
 * paidAmount / balance / paymentStatus.
 */

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const inr = (n: number) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};
const METHODS = ['Cash', 'UPI', 'Card', 'Wallet', 'Bank Transfer', 'Cheque'];
// The /pay action only accepts UPI|Cash|Card|Wallet|GiftCard|Other — map the
// friendly extras so a payment never 400s on an "invalid_method".
const API_METHODS = new Set(['UPI', 'Cash', 'Card', 'Wallet', 'GiftCard', 'Other']);
const toApiMethod = (m: string) => (API_METHODS.has(m) ? m : 'Other');

export default function SalesPaymentComposer() {
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [payNumber, setPayNumber] = useState(isEdit ? '' : `PAY-${Date.now().toString(36).toUpperCase().slice(-6)}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceId, setInvoiceId] = useState<number | string | null>(null);
  const [invoiceLabel, setInvoiceLabel] = useState('');
  const [invoice, setInvoice] = useState<Record<string, unknown> | null>(null);
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState('Cash');
  const [txnRef, setTxnRef] = useState('');
  const [editRowId, setEditRowId] = useState<number | string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const grand = Number(invoice?.grandTotal ?? 0);
  const alreadyPaid = Number(invoice?.paidAmount ?? 0);
  const balance = round2(Number(invoice?.balance ?? (grand - alreadyPaid)));

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = pickList((await apiClient.get('/sales/SalesPayment')).data);
        const row = rows.find((x) => String(x.code ?? '') === String(id) || String(x.id ?? '') === String(id));
        if (!row || cancelled) return;
        setEditRowId((row.id ?? null) as number | string | null);
        setPayNumber(String(row.code ?? id));
        if (row.paymentDate) setDate(String(row.paymentDate).slice(0, 10));
        setMethod(String(row.method ?? 'Cash'));
        setAmount(Number(row.amount ?? 0));
        setTxnRef(String(row.txnRef ?? ''));
        if (row.salesInvoiceId != null) { await loadInvoice(row.salesInvoiceId as number); }
      } catch { /* blank */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, id]);

  async function loadInvoice(invId: number | string) {
    try {
      const rows = pickList((await apiClient.get('/sales/SalesInvoice')).data);
      const inv = rows.find((x) => String(x.id) === String(invId));
      if (inv) { setInvoice(inv); setInvoiceId(inv.id as number); setInvoiceLabel(String(inv.invoiceNumber ?? inv.code ?? `#${invId}`)); }
    } catch { /* keep */ }
  }

  function pickInvoice(inv: Record<string, unknown> | null) {
    if (!inv) { setInvoice(null); setInvoiceId(null); setInvoiceLabel(''); return; }
    setInvoice(inv);
    setInvoiceId((inv.id ?? null) as number | null);
    setInvoiceLabel(String(inv.invoiceNumber ?? inv.code ?? `#${inv.id}`));
    const bal = Number(inv.balance ?? (Number(inv.grandTotal ?? 0) - Number(inv.paidAmount ?? 0)));
    setAmount(round2(Math.max(0, bal)));
  }

  async function save() {
    if (!invoiceId) { setErr('Select the invoice this payment is for.'); return; }
    if (!(amount > 0)) { setErr('Enter a payment amount.'); return; }
    setSaving(true); setErr(null);
    try {
      const amt = round2(amount);
      const isFull = Math.abs(balance - amt) <= 0.01;

      // FULL payment on a NEW record → use the dedicated /pay action. It
      // creates the SalesPayment AND updates the invoice's paid/balance/status
      // IN PLACE (server-side, transactional) — it does NOT touch
      // invoiceNumber/subTotal/grandTotal. This replaces the old flow whose
      // partial PUT to /sales/SalesInvoice (full-record replace) NULLed the
      // invoice number + totals.
      if (!isEdit && isFull && invoiceId != null) {
        try {
          await apiClient.post(`/sales/SalesInvoice/${Number(invoiceId)}/pay`, {
            method: toApiMethod(method),
            amount: amt,
            txnRef: txnRef || undefined,
          });
          toast.success('Payment recorded', `₹${inr(amt)} against ${invoiceLabel}`);
          navigate('/panel/api/sales/SalesPayment');
          return;
        } catch {
          // Fall through to the safe manual path (e.g. amount_mismatch / older
          // backend without the action) — never the wipe-causing partial PUT.
        }
      }

      // Partial payment, edit, or /pay unavailable → record the payment row,
      // then update the invoice with a FULL-ROW spread (never a partial PUT).
      const payload: Record<string, unknown> = {
        code: payNumber, name: `Payment ${payNumber}`, status: 0,
        salesInvoiceId: Number(invoiceId),
        paymentDate: new Date(date).toISOString(),
        method, amount: amt, currency: 'INR',
        txnRef: txnRef || undefined, paymentStatus: 'Completed',
      };
      if (isEdit) {
        await apiClient.put('/sales/SalesPayment', { ...payload, ...(editRowId != null ? { id: Number(editRowId) } : {}), code: payNumber });
      } else {
        await apiClient.post('/sales/SalesPayment', payload);
      }
      if (invoice?.id != null) {
        // Re-fetch the authoritative invoice row and spread the WHOLE thing into
        // the PUT so no column is nulled (the backend PUT is a full replace).
        let base: Record<string, unknown> = invoice;
        try {
          const fresh = pickList((await apiClient.get('/sales/SalesInvoice')).data).find((x) => String(x.id) === String(invoice.id));
          if (fresh) base = fresh;
        } catch { /* use the cached row */ }
        const grandNow = Number(base.grandTotal ?? grand);
        const newPaid = round2(Number(base.paidAmount ?? alreadyPaid) + (isEdit ? 0 : amt));
        const newBalance = round2(Math.max(0, grandNow - newPaid));
        // Drop server-managed audit cols (a stale updatedAt can trip
        // optimistic-concurrency → the update silently reverts).
        const { updatedAt, indate, createdBy, updatedBy, deletedAt, deletedBy, ...clean } = base;
        void updatedAt; void indate; void createdBy; void updatedBy; void deletedAt; void deletedBy;
        await apiClient.put('/sales/SalesInvoice', {
          ...clean,
          id: Number(base.id),
          paidAmount: newPaid,
          balance: newBalance,
          paymentStatus: paymentStatusFor(newPaid, grandNow),
        }).catch(() => undefined);
      }
      toast.success('Payment recorded', `₹${inr(amt)} against ${invoiceLabel}`);
      navigate('/panel/api/sales/SalesPayment');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg); toast.error('Save failed', msg);
    } finally { setSaving(false); }
  }

  const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary';
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-1">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/panel/api/sales/SalesPayment')} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back"><ArrowLeft size={16} /></button>
        <h1 className="text-base font-semibold">{isEdit ? 'Edit' : 'Record'} Sales Payment</h1>
      </div>
      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Payment No"><input className={inputCls} value={payNumber} onChange={(e) => setPayNumber(e.target.value)} /></Field>
          <Field label="Date"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Against Invoice">
            <EntityPicker<Record<string, unknown>>
              basePath="/sales/SalesInvoice" idKey="id" labelKey="invoiceNumber" secondaryKey="customerName"
              value={invoiceId}
              selectedRow={invoiceLabel ? ({ id: invoiceId, invoiceNumber: invoiceLabel } as Record<string, unknown>) : null}
              onChange={pickInvoice} placeholder="Select invoice" />
          </Field>
        </div>
        {invoice && (
          <div className="grid gap-2 rounded-md border border-border bg-surface-app p-3 text-sm sm:grid-cols-4">
            <Kv label="Customer" value={String(invoice.customerName ?? '—')} />
            <Kv label="Invoice Total" value={`₹ ${inr(grand)}`} />
            <Kv label="Already Paid" value={`₹ ${inr(alreadyPaid)}`} />
            <Kv label="Outstanding" value={`₹ ${inr(balance)}`} danger={balance > 0} />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Amount Received">
            <div className="flex gap-1">
              <input type="number" className={inputCls} value={amount || ''} onChange={(e) => setAmount(Number(e.target.value) || 0)} placeholder="0" />
              {invoice && <button type="button" className="shrink-0 rounded border border-border px-2 text-xs text-primary hover:bg-surface-card-hover" onClick={() => setAmount(balance)}>Full</button>}
            </div>
          </Field>
          <Field label="Method">
            <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Txn Reference"><input className={inputCls} value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="UTR / cheque no" /></Field>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/panel/api/sales/SalesPayment')} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Record Payment'}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-content-secondary">{label}</span>{children}</label>;
}
function Kv({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return <div><div className="text-[10px] uppercase tracking-wide text-content-muted">{label}</div><div className={`font-medium ${danger ? 'text-danger' : 'text-content-primary'}`}>{value}</div></div>;
}
